/* eslint-disable no-restricted-syntax */

import { useMemo } from 'react';
import { getArraySort, sortByChars } from '@gp/utils/common';
import { OHERSKEY } from '@gp/constants/const';
import { GROUP_KEY } from '@gp/constants/select';
import { KEYS_MAPPING, getFilterInterWords } from '@gp/constants/field';
import { getOptionsAndValues, getStageOptionsAndValues } from '../components/Filter/utils';
import {
  renameOfProjectGroupOptions,
  getCascadeProjectDatas,
  getCascadeProjectValues,
  formStatusOptions,
} from '@gp/utils/project';

export interface IScheduleFilterMetadata<T> {
  pteam: T[];
  business_owner: T[];
  studio: T[];
  project: T[];
  platform: T[];
  publisher: T[];
  status: T[];
  stage: T[];
  form: T[];
}

interface IScheduleFilterOptionsAndValues {
  options: IScheduleFilterMetadata<IGPSelectOptionWithGroup>;
  values: IScheduleFilterMetadata<string>;
}

const SORTED_COMMON_FIELDS = [
  KEYS_MAPPING.PLATFORM,
  KEYS_MAPPING.STATUS,
  KEYS_MAPPING.PUBLISHER,
  KEYS_MAPPING.P_TEAM,
];
const COMMON_FIELDS = [...SORTED_COMMON_FIELDS, KEYS_MAPPING.GROUP, KEYS_MAPPING.STAGE];
const MAPPING_FIELDS = [KEYS_MAPPING.STUDIO, KEYS_MAPPING.PROJECT];

const getEmptyOptionsValues = <T = any>(): IScheduleFilterMetadata<T> => ({
  pteam: [],
  business_owner: [],
  studio: [],
  project: [],
  platform: [],
  publisher: [],
  status: [],
  stage: [],
  form: [],
});

export default function useOptionsAndValues(loading?: boolean, filterList?: any) {
  const changeOptions = (fieldKey: any, fieldOptions: any) => {
    switch (fieldKey) {
      case KEYS_MAPPING.GROUP:
        return renameOfProjectGroupOptions(fieldOptions);
      case KEYS_MAPPING.STATUS:
        return formStatusOptions(fieldOptions);
    }
    return fieldOptions;
  };

  // @ts-ignore
  const { options, values, defaultValues } = useMemo<IScheduleFilterOptionsAndValues>(() => {
    if (loading) return { options: [], values: [], defaultValues: {} };
    const { pteam_bo_studio_project: pbsProject = {} } = filterList || {};
    const { pteam_list: pteamList = [] } = pbsProject || {};
    const options: any = getEmptyOptionsValues<IGPSelectOptionWithGroup>();
    const values: any = getEmptyOptionsValues<string>();
    const defaultValues: any = {};

    // project配置项及初始化全选
    if (Array.isArray(pteamList) && pteamList.length > 0) {
      const cascadeProjectoptions = getCascadeProjectDatas(pteamList, 'sensitive_filter_auth');
      options.project_id = cascadeProjectoptions || [];
      values.project_id = getCascadeProjectValues(cascadeProjectoptions, true);
      defaultValues.project_id = getCascadeProjectValues(cascadeProjectoptions, false);
    }

    // mapping类型的字段
    MAPPING_FIELDS.forEach((field) => {
      // const field = getFilterInterWords(fieldItem);
      // @ts-ignore
      const fieldData = filterList?.[getFilterInterWords(field)];
      if (fieldData) {
        Object.entries(fieldData).forEach(([key, value]) => {
          // @ts-ignore
          options[field].push({
            groupKey: GROUP_KEY,
            value,
            text: key || OHERSKEY,
            label: key || OHERSKEY,
          });
          // @ts-ignore
          values[field].push(value);
        });
      }
    });

    // 常规字段
    COMMON_FIELDS.forEach((field) => {
      const getFn = field === KEYS_MAPPING.STAGE ? getStageOptionsAndValues : getOptionsAndValues;
      // @ts-ignore
      const { options: fieldOptions, values: fieldValues } = getFn(
        filterList?.[getFilterInterWords(field)],
      );
      // @ts-ignore
      options[field] = changeOptions(field, fieldOptions);
      // @ts-ignore
      values[field] = fieldValues;
    });

    return { options, values, defaultValues };
  }, [loading, filterList]);

  const sortedOptions = useMemo<IScheduleFilterMetadata<IGPSelectOptionWithGroup>>(() => {
    if (loading) return options;

    const result: IScheduleFilterMetadata<IGPSelectOptionWithGroup> = {
      pteam: [],
      business_owner: [],
      studio: [],
      project: [],
      platform: [],
      publisher: [],
      status: [],
      stage: [],
      form: [],
    };

    for (const optionsKey in options) {
      const optionsList = (options as any)?.[optionsKey];
      if (COMMON_FIELDS.includes(optionsKey)) {
        (result as any)[optionsKey] = Array.isArray(optionsList) ? optionsList : [];
        if (SORTED_COMMON_FIELDS.includes(optionsKey)) {
          (result as any)[optionsKey] = sortByChars('text', optionsList);
        }
        continue;
      }
      if (!optionsList?.length) {
        (result as any)[optionsKey] = [];
      } else {
        let othersItem = null;
        // 过滤掉Others项
        const filterOpt =
          optionsList?.filter((a: any) => {
            if (a?.text === OHERSKEY) {
              othersItem = a;
              return false;
            }
            return true;
          }) || [];
        // 排序
        const tempSort = getArraySort(filterOpt, 'string', 'asc', 'text');
        (result as any)[optionsKey] = othersItem ? [...tempSort, othersItem] : tempSort;
      }
    }

    return result;
  }, [loading, options]);

  return { options: sortedOptions, values, defaultValues };
}
